import React from 'react';
import { StyleSheet, Text, TextInput } from 'react-native';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import type { LocalizedMessage } from '../../lib/i18n/messages';
import colors from '../../lib/colors';
import VerificationCodePrompt from '../VerificationCodePrompt';
import Button from '../ui/Button';
import Card from '../ui/Card';

interface EmailCaptureProps {
    email: string;
    isRequesting: boolean;
    onChangeEmail: (email: string) => void;
    onRequestVerification: () => void;
}

export function ProjectActivationEmailCapture({
    email,
    isRequesting,
    onChangeEmail,
    onRequestVerification,
}: EmailCaptureProps) {
    const { i18n } = useLingui();

    return (
        <Card style={styles.captureCard}>
            <Text style={styles.cardTitle}><Trans id="onboarding.projectactivationscreen.whereShouldWeSendYourFirstMatchingPapers" comment="UI copy in components/onboarding/ProjectActivationScreen.tsx">Where should we send your first matching papers?</Trans></Text>
            <Text style={styles.cardMessage}><Trans id="onboarding.projectactivationscreen.confirmYourEmailToKeepThisProjectAndOpenA" comment="UI copy in components/onboarding/ProjectActivationScreen.tsx">
                Confirm your email to keep this project and open a one-time preview. No trial, charge, or spam.
            </Trans></Text>
            <Text style={styles.label}><Trans id="onboarding.projectactivationscreen.emailAddress" comment="UI copy in components/onboarding/ProjectActivationScreen.tsx">Email address</Trans></Text>
            <TextInput
                style={styles.emailInput}
                value={email}
                onChangeText={onChangeEmail}
                placeholder={i18n._(msg({ id: 'common.emailPlaceholder', message: 'you@example.com' }))}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="emailAddress"
                autoComplete="email"
                accessibilityLabel={i18n._(msg({
                    id: 'onboarding.activation.emailAccessibilityLabel',
                    message: 'Email address for your first matching papers',
                }))}
                testID="activation-email-input"
                onSubmitEditing={onRequestVerification}
            />
            <Button
                label={isRequesting
                    ? msg({ id: 'onboarding.activation.requesting', message: 'Requesting…' })
                    : msg({ id: 'onboarding.activation.requestCode', message: 'Request confirmation code' })}
                onPress={onRequestVerification}
                disabled={isRequesting}
                testID="activation-send-code"
            />
        </Card>
    );
}

interface CodePromptProps {
    email: string;
    isRequesting: boolean;
    requestError: LocalizedMessage | null;
    resendCooldown: number;
    onVerified: () => Promise<void> | void;
    onResend: () => void;
    onUseDifferentEmail: () => void;
}

export function ProjectActivationCodePrompt({
    email,
    isRequesting,
    requestError,
    resendCooldown,
    onVerified,
    onResend,
    onUseDifferentEmail,
}: CodePromptProps) {
    return (
        <Card style={styles.verifyCard}>
            <Text style={styles.cardTitle} accessibilityRole="header"><Trans id="onboarding.projectactivationscreen.confirmEmailAndOpenMyMatches" comment="UI copy in components/onboarding/ProjectActivationScreen.tsx">
                Confirm email and open my matches
            </Trans></Text>
            <VerificationCodePrompt
                email={email}
                emailLabel="Email address:"
                otpType="email"
                instructionText="If a code arrived, enter the latest 6-digit code. Confirmation does not start a trial or charge you."
                onVerified={onVerified}
                onResend={onResend}
                resendLabel="Request another code"
                resendCooldownLabel="Another code request available"
                resendCooldown={resendCooldown}
                resendLoading={isRequesting}
                resendError={requestError}
                resendTestID="activation-resend-code"
                secondaryActionLabel={msg({
                    id: 'auth.code.useDifferentEmail',
                    message: 'Use a different email',
                })}
                onSecondaryAction={onUseDifferentEmail}
                secondaryActionTestID="activation-use-different-email"
            />
        </Card>
    );
}

const styles = StyleSheet.create({
    captureCard: { gap: 14 },
    verifyCard: { alignItems: 'center', gap: 8 },
    cardTitle: { color: colors.text900, fontSize: 21, fontWeight: '700', lineHeight: 28 },
    cardMessage: { color: colors.text600, fontSize: 15, lineHeight: 22 },
    label: { color: colors.text700, fontSize: 14, fontWeight: '600' },
    emailInput: {
        backgroundColor: '#ffffff',
        borderColor: colors.border,
        borderRadius: 10,
        borderWidth: 1,
        color: colors.text900,
        fontSize: 16,
        paddingHorizontal: 14,
        paddingVertical: 13,
    },
});
